import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";

/** /admin — founder console (CHAT_ADMIN_EMAILS accounts only; everyone else gets 403 and a
 *  polite wall). Grant free seats, timeskip/reset trials, suspend, confirm mail, delete rows. */

interface AdminUser {
  email: string;
  contactEmail: string | null;
  plan: string;
  status: string;
  role: string;
  emailConfirmed: boolean;
  compUntil: number | null;
  referredBy: string | null;
  registeredAt: number | null;
  firstLoginAt: number | null;
  lastSeenAt: number | null;
  subscriptionStatus: string | null;
}

interface Referral {
  code: string;
  ownerEmail: string;
  note: string | null;
  createdAt: number;
  signups: number;
  paying: number;
  link: string;
}

interface Invite {
  token: string;
  link: string;
  grantType: string;
  createdBy: string;
  note: string | null;
  createdAt: number;
  usedBy: string | null;
  usedAt: number | null;
}

const INVITE_TYPES: Array<{ key: string; label: string }> = [
  { key: "comp_month", label: "1 MONTH" },
  { key: "press", label: "PRESS" },
  { key: "creator", label: "CREATOR" },
  { key: "internal", label: "INTERNAL" },
];

const ACTIONS: Array<{ key: string; label: string; title: string }> = [
  { key: "grant_internal", label: "INTERNAL", title: "free forever, plan=internal (team) — never billed, no founding seat" },
  { key: "grant_press", label: "PRESS", title: "free forever, plan=press (journalists) — never billed, no founding seat" },
  { key: "grant_creator", label: "CREATOR", title: "free forever, plan=creator (streamers/influencers) — never billed, no founding seat" },
  { key: "comp_month", label: "FREE MONTH", title: "+30 days complimentary access (click again to stack); lapses back to the paywall by itself" },
  { key: "expire_trial", label: "TIMESKIP", title: "expire the trial now (paywall on next request)" },
  { key: "reset_trial", label: "RESET TRIAL", title: "fresh 1h trial starting now" },
  { key: "suspend", label: "SUSPEND", title: "cut access now" },
  { key: "reactivate", label: "REACTIVATE", title: "status → active (plan untouched)" },
  { key: "confirm_email", label: "CONFIRM MAIL", title: "mark the email confirmed manually" },
  { key: "delete", label: "DELETE", title: "remove the DB row (click twice; Firebase identity survives)" },
];

/** Superadmin-only (SUPERADMIN_EMAILS env): one toggle per row based on the current role. */
const superAction = (role: string) =>
  role === "admin"
    ? [{ key: "revoke_admin", label: "REVOKE ADMIN", title: "role → user (env-rooted admins unaffected)" }]
    : [{ key: "make_admin", label: "MAKE ADMIN", title: "grant chat moderation + /admin console access" }];

const cell: React.CSSProperties = { padding: "5px 8px", borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap" };
const ago = (ms: number | null) => {
  if (!ms) return "—";
  const s = (Date.now() - ms) / 1000;
  return s < 3600 ? `${Math.floor(s / 60)}m` : s < 86400 ? `${Math.floor(s / 3600)}h` : `${Math.floor(s / 86400)}d`;
};

export function AdminScreen() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [denied, setDenied] = useState(false);
  const [superadmin, setSuperadmin] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [armDelete, setArmDelete] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [refCode, setRefCode] = useState("");
  const [refOwner, setRefOwner] = useState("");
  const [refNote, setRefNote] = useState("");
  const [invites, setInvites] = useState<Invite[]>([]);
  const [invNote, setInvNote] = useState("");

  const load = useCallback(async (query: string) => {
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/admin/users?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 403) { setDenied(true); return; }
      const j = (await r.json()) as { users?: AdminUser[]; superadmin?: boolean };
      setUsers(j.users ?? []);
      setSuperadmin(j.superadmin === true);
      const rr = await fetch(`${config.restUrl}/api/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (rr.ok) setReferrals(((await rr.json()) as { referrals?: Referral[] }).referrals ?? []);
      const ri = await fetch(`${config.restUrl}/api/admin/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (ri.ok) setInvites(((await ri.json()) as { invites?: Invite[] }).invites ?? []);
    } catch {
      setNote("load failed — is the API reachable?");
    }
  }, []);

  async function createInvite(grantType: string) {
    setNote(null);
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/admin/invites`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ grantType, note: invNote }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; link?: string };
      if (r.ok && j.link) {
        await navigator.clipboard.writeText(j.link).catch(() => {});
        setNote(`invite created + copied: ${j.link}`);
        setInvNote("");
        await load(q);
      } else {
        setNote(`invite failed: ${j.error ?? r.status}`);
      }
    } catch {
      setNote("invite failed — network");
    }
  }

  async function deleteInvite(token0: string) {
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/admin/invites/${encodeURIComponent(token0)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNote(r.ok ? "invite revoked" : "revoke failed (used invites are permanent history)");
      await load(q);
    } catch {
      setNote("revoke failed — network");
    }
  }

  async function createReferral() {
    setNote(null);
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/admin/referrals`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ code: refCode, ownerEmail: refOwner, note: refNote }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; link?: string };
      setNote(r.ok ? `referral created: ${j.link}` : `referral failed: ${j.error ?? r.status}`);
      if (r.ok) { setRefCode(""); setRefOwner(""); setRefNote(""); await load(q); }
    } catch {
      setNote("referral failed — network");
    }
  }

  useEffect(() => { void load(""); }, [load]);

  async function act(email: string, action: string) {
    if (action === "delete" && armDelete !== email) { setArmDelete(email); return; }
    setArmDelete(null);
    setBusy(email + action);
    setNote(null);
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/admin/users/action`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ email, action }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setNote(r.ok ? `${action} → ${email} ✓` : `${action} failed: ${j.error ?? r.status}`);
      await load(q);
    } catch {
      setNote(`${action} failed — network`);
    }
    setBusy(null);
  }

  if (denied) {
    return (
      <div id="viewport" className="vp-fluid">
        <div className="legal">
          <h1>ADMIN</h1>
          <p>This console is for desk admins only.</p>
          <Link className="back" to="/ops">← Back to ops</Link>
        </div>
      </div>
    );
  }

  return (
    <div id="viewport" className="vp-fluid">
      <div style={{ padding: "26px 30px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--ivory)", maxWidth: 1300, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 14 }}>
          <h1 style={{ fontFamily: "var(--disp)", fontSize: 18, letterSpacing: "0.12em", margin: 0 }}>USER ADMIN</h1>
          <span style={{ color: "var(--ivory-d)" }}>{users ? `${users.length} account(s)` : "loading…"}</span>
          <Link to="/ops" style={{ marginLeft: "auto", color: "var(--green)" }}>← ops</Link>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void load(q); }}
            placeholder="filter by email — Enter to search"
            style={{ flex: 1, maxWidth: 380, background: "rgba(6,12,9,0.7)", border: "1px solid var(--line-2)", color: "var(--ivory)", padding: "7px 10px", fontFamily: "var(--mono)", fontSize: 12 }}
          />
          <button className="mbtn" onClick={() => void load(q)}>SEARCH</button>
          <button className="mbtn" onClick={() => { setQ(""); void load(""); }}>ALL</button>
        </div>
        {note && <div style={{ color: "var(--amber)", marginBottom: 10 }}>{note}</div>}
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr style={{ color: "var(--ivory-dd)", textAlign: "left", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                <th style={cell}>email</th>
                <th style={cell}>plan</th>
                <th style={cell}>status</th>
                <th style={cell}>role</th>
                <th style={cell}>mail</th>
                <th style={cell}>sub</th>
                <th style={cell}>ref</th>
                <th style={cell}>reg</th>
                <th style={cell}>seen</th>
                <th style={cell}>actions</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u) => (
                <tr key={u.email}>
                  <td style={cell} title={u.email}>{u.contactEmail ?? u.email}</td>
                  <td style={{ ...cell, color: u.plan === "internal" ? "var(--cyan, #5fd3e0)" : undefined }}>{u.plan}</td>
                  <td style={{ ...cell, color: u.status === "active" ? "var(--green)" : u.status === "suspended" || u.status === "blocked" ? "var(--red, #ff4438)" : "var(--amber)" }}>
                    {u.status}
                  </td>
                  <td style={{ ...cell, color: u.role === "admin" ? "var(--cyan, #5fd3e0)" : "var(--ivory-dd)" }}>{u.role}</td>
                  <td style={cell}>{u.emailConfirmed ? "✓" : "…"}</td>
                  <td style={cell}>
                    {u.subscriptionStatus ??
                      (u.compUntil && u.compUntil > Date.now()
                        ? `comp ${Math.ceil((u.compUntil - Date.now()) / 86_400_000)}d`
                        : "—")}
                  </td>
                  <td style={{ ...cell, color: "var(--ivory-dd)" }}>{u.referredBy ?? "—"}</td>
                  <td style={cell}>{ago(u.registeredAt)}</td>
                  <td style={cell}>{ago(u.lastSeenAt)}</td>
                  <td style={{ ...cell, display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {[...ACTIONS, ...(superadmin ? superAction(u.role) : [])].map((a) => (
                      <button
                        key={a.key}
                        className="mbtn"
                        title={a.title}
                        disabled={busy === u.email + a.key}
                        onClick={() => void act(u.email, a.key)}
                        style={{
                          padding: "3px 7px", fontSize: 9,
                          ...(a.key === "delete" ? { color: armDelete === u.email ? "#fff" : "var(--red, #ff4438)", borderColor: "rgba(255,68,56,0.5)", background: armDelete === u.email ? "rgba(255,68,56,0.35)" : undefined } : {}),
                        }}
                      >
                        {a.key === "delete" && armDelete === u.email ? "SURE?" : a.label}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ color: "var(--ivory-dd)", fontSize: 10, marginTop: 12 }}>
          INTERNAL/PRESS/CREATOR = free forever with an honest plan label (never billed, no founding
          seat). TIMESKIP expires the trial instantly. DELETE removes only our row — the Firebase
          login survives and re-provisions a fresh trial on next sign-in.
        </p>

        <div style={{ display: "flex", alignItems: "baseline", gap: 16, margin: "26px 0 10px" }}>
          <h2 style={{ fontFamily: "var(--disp)", fontSize: 14, letterSpacing: "0.12em", margin: 0 }}>INVITES</h2>
          <span style={{ color: "var(--ivory-dd)", fontSize: 10 }}>
            single-use registration links — the grant applies the moment the invitee signs up; link auto-copies on create
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input value={invNote} onChange={(e) => setInvNote(e.target.value)} placeholder="note (who is this for)"
            style={{ background: "rgba(6,12,9,0.7)", border: "1px solid var(--line-2)", color: "var(--ivory)", padding: "7px 10px", fontFamily: "var(--mono)", fontSize: 12, flex: 1, minWidth: 200, maxWidth: 380 }} />
          {INVITE_TYPES.map((t) => (
            <button key={t.key} className="mbtn" title={`create a single-use ${t.label} invite link`} onClick={() => void createInvite(t.key)}>
              + {t.label}
            </button>
          ))}
        </div>
        {invites.length > 0 && (
          <div style={{ overflowX: "auto", marginBottom: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ color: "var(--ivory-dd)", textAlign: "left", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  <th style={cell}>grant</th>
                  <th style={cell}>status</th>
                  <th style={cell}>by</th>
                  <th style={cell}>age</th>
                  <th style={cell}>note</th>
                  <th style={cell}>actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.token}>
                    <td style={{ ...cell, color: "var(--cyan, #5fd3e0)" }}>{inv.grantType}</td>
                    <td style={{ ...cell, color: inv.usedBy ? "var(--green)" : "var(--amber)" }}>
                      {inv.usedBy ? `USED · ${inv.usedBy}` : "unused"}
                    </td>
                    <td style={cell}>{inv.createdBy}</td>
                    <td style={cell}>{ago(inv.createdAt)}</td>
                    <td style={{ ...cell, color: "var(--ivory-dd)", whiteSpace: "normal" }}>{inv.note ?? "—"}</td>
                    <td style={{ ...cell, display: "flex", gap: 4 }}>
                      {!inv.usedBy && (
                        <>
                          <button className="mbtn" style={{ padding: "3px 7px", fontSize: 9 }}
                            onClick={() => { void navigator.clipboard.writeText(inv.link).then(() => setNote(`copied ${inv.link}`)); }}>
                            COPY LINK
                          </button>
                          <button className="mbtn" style={{ padding: "3px 7px", fontSize: 9, color: "var(--red, #ff4438)", borderColor: "rgba(255,68,56,0.5)" }}
                            onClick={() => void deleteInvite(inv.token)}>
                            REVOKE
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "baseline", gap: 16, margin: "26px 0 10px" }}>
          <h2 style={{ fontFamily: "var(--disp)", fontSize: 14, letterSpacing: "0.12em", margin: 0 }}>REFERRALS</h2>
          <span style={{ color: "var(--ivory-dd)", fontSize: 10 }}>
            hand a creator their link (earlywire.io/?ref=code); signups + paying conversions are attributed automatically
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input value={refCode} onChange={(e) => setRefCode(e.target.value)} placeholder="code (a-z 0-9 - _)"
            style={{ background: "rgba(6,12,9,0.7)", border: "1px solid var(--line-2)", color: "var(--ivory)", padding: "7px 10px", fontFamily: "var(--mono)", fontSize: 12, width: 170 }} />
          <input value={refOwner} onChange={(e) => setRefOwner(e.target.value)} placeholder="owner email (gets the kickback)"
            style={{ background: "rgba(6,12,9,0.7)", border: "1px solid var(--line-2)", color: "var(--ivory)", padding: "7px 10px", fontFamily: "var(--mono)", fontSize: 12, width: 250 }} />
          <input value={refNote} onChange={(e) => setRefNote(e.target.value)} placeholder="note (deal terms, channel)"
            style={{ background: "rgba(6,12,9,0.7)", border: "1px solid var(--line-2)", color: "var(--ivory)", padding: "7px 10px", fontFamily: "var(--mono)", fontSize: 12, flex: 1, minWidth: 180 }} />
          <button className="mbtn" onClick={() => void createReferral()}>CREATE</button>
        </div>
        {referrals.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ color: "var(--ivory-dd)", textAlign: "left", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  <th style={cell}>code</th>
                  <th style={cell}>owner</th>
                  <th style={cell}>signups</th>
                  <th style={cell}>paying</th>
                  <th style={cell}>link</th>
                  <th style={cell}>note</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.code}>
                    <td style={{ ...cell, color: "var(--green)" }}>{r.code}</td>
                    <td style={cell}>{r.ownerEmail}</td>
                    <td style={cell}>{r.signups}</td>
                    <td style={{ ...cell, color: r.paying > 0 ? "var(--green)" : undefined }}>{r.paying}</td>
                    <td style={cell}>
                      <button className="mbtn" style={{ padding: "3px 7px", fontSize: 9 }}
                        onClick={() => { void navigator.clipboard.writeText(r.link).then(() => setNote(`copied ${r.link}`)); }}>
                        COPY LINK
                      </button>
                    </td>
                    <td style={{ ...cell, color: "var(--ivory-dd)", whiteSpace: "normal" }}>{r.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
